import { ArrowLeft, Upload } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import { AppShell } from "@/components/AppShell";
import { FormBasedFlowBuilder } from "@/components/flow-builder/FormBasedFlowBuilder";
import { PageSkeleton } from "@/components/page-skeleton";
import { StatusPill } from "@/components/status-pill";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { useFlowGraph, useSaveFlowGraph, useCategories, usePublishFlow, useUpdateFlow } from "@/hooks/api/flows";
import { apiErrorList, apiErrorMessage } from "@/lib/api";
import type { FlowNode, FlowEdge } from "@/types/api";

/**
 * Flow Builder Page (Unified)
 *
 * Replaced flowchart builder with form-based builder.
 * Now handles both creating new flows and editing existing flows.
 * Accessible at:
 * - /flows/:id/builder (edit existing flow)
 */

function FlowBuilderInner() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [flowTitle, setFlowTitle] = useState("");
  const [categoryId, setCategoryId] = useState("");

  const { data: flow, isLoading: flowLoading } = useFlowGraph(id);
  const { data: categoriesData } = useCategories();
  const saveFlowGraph = useSaveFlowGraph(id);
  const updateFlow = useUpdateFlow(id || "");
  const publishFlow = usePublishFlow();

  // Pre-fill if editing
  useEffect(() => {
    if (flow && id) {
      setFlowTitle(flow.flow.title);
      setCategoryId(flow.flow.category.id);
    }
  }, [flow, id]);

  const handleSave = async (flowData: { nodes: FlowNode[]; edges: FlowEdge[] }) => {
    if (!flowTitle.trim()) {
      toast.error("Please enter a script name");
      return;
    }

    if (!categoryId) {
      toast.error("Please select a category");
      return;
    }

    try {
      const payload = {
        nodes: flowData.nodes.map((n) => ({
          client_id: n.id,
          node_type: n.node_type,
          title: n.title,
          title_es: n.title_es,
          spiel_text: n.spiel_text,
          spiel_text_es: n.spiel_text_es,
          internal_note: n.internal_note,
          internal_note_es: n.internal_note_es,
          condition_text: n.condition_text,
          condition_text_es: n.condition_text_es,
          builder_step_type: n.builder_step_type,
          position_x: n.position_x,
          position_y: n.position_y,
        })),
        edges: flowData.edges.map((e) => ({
          source_node: e.source_node,
          target_node: e.target_node,
          label: e.label,
          label_es: e.label_es,
          sort_order: e.sort_order,
        })),
      };

      // Save graph structure
      await saveFlowGraph.mutateAsync(payload);

      // If editing, also update metadata (title, category)
      if (id) {
        await updateFlow.mutateAsync({
          title: flowTitle,
          title_es: flowTitle,
          category_id: categoryId,
        });
      }

      toast.success("Workflow saved!");
    } catch (error) {
      const msg = apiErrorMessage(error, "Failed to save workflow");
      toast.error(msg);
    }
  };

  const handlePublish = async () => {
    if (!id) {
      toast.error("Workflow ID not found");
      return;
    }

    try {
      await publishFlow.mutateAsync(id);
      toast.success("Workflow published!");
      // Reload flow data to update status
      window.location.reload();
    } catch (error) {
      // validate_flow_for_publish reports every problem it finds in one
      // pass (every unreachable step, every dead end) — surface all of
      // them at once rather than just the first, so fixing the script
      // doesn't turn into a "fix one, republish, discover the next" loop.
      const issues = apiErrorList(error);
      if (issues.length > 1) {
        toast.error(`Can't publish yet — ${issues.length} issues to fix`, {
          description: (
            <ul className="list-disc space-y-0.5 pl-4">
              {issues.map((issue, i) => (
                <li key={i}>{issue}</li>
              ))}
            </ul>
          ),
          duration: Infinity,
        });
      } else {
        toast.error(apiErrorMessage(error, "Failed to publish workflow"));
      }
    }
  };

  if (flowLoading) {
    return <PageSkeleton />;
  }

  if (!flow) {
    return (
      <div className="text-center py-8">
        <p className="text-slate-600 mb-4">Workflow not found</p>
        <Button onClick={() => navigate("/flows")}>Back to Workflows</Button>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Button asChild variant="ghost" size="icon">
            <Link to="/flows" aria-label="Back to workflows">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-semibold">Edit Workflow</h1>
              {flow && (
                <StatusPill kind={flow.flow.status === "PUBLISHED" ? "success" : "pending"}>
                  {flow.flow.status}
                </StatusPill>
              )}
            </div>
            <p className="text-sm text-slate-600">
              Modify your workflow with form-based steps
            </p>
          </div>
        </div>
        {flow?.flow.status === "DRAFT" && (
          <Button
            onClick={handlePublish}
            loading={publishFlow.isPending}
          >
            <Upload className="h-4 w-4 mr-2" />
            Publish
          </Button>
        )}
      </div>

      {/* Basic Info Card */}
      <Card className="p-4 mb-4">
        <h2 className="font-semibold mb-3">Workflow Details</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs">Workflow Name</Label>
            <Input
              value={flowTitle}
              onChange={(e) => setFlowTitle(e.target.value)}
              placeholder="e.g., Standard Support Call"
            />
          </div>

          <div>
            <Label className="text-xs">Category</Label>
            <Select value={categoryId} onValueChange={setCategoryId}>
              <SelectTrigger>
                <SelectValue placeholder="Select a category" />
              </SelectTrigger>
              <SelectContent>
                {categoriesData?.results.map((cat) => (
                  <SelectItem key={cat.id} value={cat.id}>
                    {cat.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      {/* Main Builder */}
      <FormBasedFlowBuilder
        onSave={handleSave}
        isSaving={saveFlowGraph.isPending}
        initialNodes={flow.nodes}
        initialEdges={flow.edges}
      />
    </div>
  );
}

export default function FlowBuilderPage() {
  return (
    <AppShell>
      <FlowBuilderInner />
    </AppShell>
  );
}
